import { ApiError } from "../../lib/api";
import { LogisticsEditor } from "./LogisticsEditor";
import { PreparationHeader } from "./PreparationHeader";
import { useTournamentPreparation } from "./useTournamentPreparation";
import "./preparation.css";
import "./preparation-edit.css";

function policyLabel(value: string) {
  if (value === "same-car") return "Ten sam samochód";
  if (value === "same-driver") return "Ten sam kierowca";
  return "Elastyczne kluczyki";
}

export function LogisticsPage() {
  const state = useTournamentPreparation();
  const data = state.preparation.data;
  const error = state.preparation.error || state.tournaments.error;
  const errorMessage = error instanceof ApiError ? error.message : error instanceof Error ? error.message : "";

  return (
    <section className="page-grid prep-page logistics-page">
      <PreparationHeader
        eyebrow="TOURNAMENT LOGISTICS"
        title={state.selectedTournament?.name || "Logistyka"}
        description="Noclegi i fizyczna flota turnieju. Edytujesz tutaj dokładnie ten model, z którego korzysta Generator, Transport i Manual Board."
        season={state.season}
        tournamentId={state.tournamentId}
        tournaments={state.tournaments.data?.tournaments || []}
        onSeasonChange={state.setSeason}
        onTournamentChange={state.setTournamentId}
      />

      {errorMessage ? <article className="panel prep-error"><h3>Nie udało się wczytać logistyki</h3><p>{errorMessage}</p></article> : null}
      {state.preparation.isPending && state.tournamentId ? <article className="panel round-loading"><div className="round-loading-bar"/><span>Wczytuję noclegi i flotę…</span></article> : null}

      {data ? (
        <>
          <div className="prep-kpi-grid logistics-kpis">
            <article className="panel prep-kpi"><span>Noclegi</span><strong>{data.summary.accommodationCount}</strong><small>{data.accommodations.filter((row) => row.active).length} aktywnych</small></article>
            <article className="panel prep-kpi"><span>Auta</span><strong>{data.summary.vehicleCount}</strong><small>{data.summary.setupVehicleCount} setup</small></article>
            <article className="panel prep-kpi"><span>Kierowcy</span><strong>{data.summary.driverCount}</strong><small>do całej floty</small></article>
            <article className="panel prep-kpi"><span>Kluczyki</span><strong className="prep-kpi-policy">{policyLabel(data.tournament.vehicleKeyPolicy)}</strong><small>revision r{data.configRevision}</small></article>
          </div>

          <LogisticsEditor tournamentId={state.tournamentId} data={data} />

          <div className="logistics-base-grid">
            {data.accommodations.map((base) => {
              const members = data.staff.filter((person) => person.baseId === base.id);
              const vehicles = data.vehicles.filter((vehicle) => vehicle.baseId === base.id && vehicle.active);
              const normal = vehicles.filter((vehicle) => !vehicle.setup);
              const setup = vehicles.filter((vehicle) => vehicle.setup);
              const seats = normal.reduce((sum, vehicle) => sum + vehicle.capacity, 0);
              const drivers = members.filter((person) => person.driverEligible).length;
              return (
                <article className={`panel logistics-base-card ${base.active ? "" : "inactive"}`} key={base.id}>
                  <header>
                    <div><span className="eyebrow">{base.type.toUpperCase()} · {base.travelMinutes} MIN</span><h3>{base.name}</h3>{base.address ? <p>{base.address}</p> : null}</div>
                    <strong>{members.length}</strong>
                  </header>
                  <div className="logistics-base-stats">
                    <span><small>Osoby</small><strong>{members.length}</strong></span>
                    <span><small>Kierowcy</small><strong>{drivers}</strong></span>
                    <span><small>Normalne auta</small><strong>{normal.length}</strong></span>
                    <span><small>Miejsca</small><strong>{seats || "—"}</strong></span>
                  </div>
                  <div className="logistics-vehicle-mini-list">
                    {vehicles.map((vehicle) => <span key={vehicle.id} className={vehicle.setup ? "setup" : ""}><strong>{vehicle.name}</strong><small>{vehicle.preferredCapacity}/{vehicle.capacity} os.{vehicle.setup ? " · SETUP" : ""}</small></span>)}
                    {!vehicles.length ? <p>Brak aktywnego auta przypisanego do tego noclegu.</p> : null}
                  </div>
                  {!base.active ? <div className="logistics-setup-note">Nocleg wyłączony z aktywnej konfiguracji</div> : null}
                  {setup.length ? <div className="logistics-setup-note">Setup vehicles: {setup.map((vehicle) => vehicle.name).join(", ")}</div> : null}
                </article>
              );
            })}
            {!data.accommodations.length ? <article className="panel prep-empty">Brak skonfigurowanych noclegów.</article> : null}
          </div>

          <article className="panel fleet-table-card">
            <header className="prep-card-head"><div><span className="eyebrow">PHYSICAL FLEET</span><h3>Flota turnieju</h3></div><p>Capacity to limit fizyczny, preferred capacity to docelowe zapełnienie używane przy układaniu transportu.</p></header>
            <div className="team-table-wrap">
              <table className="team-table fleet-table">
                <thead><tr><th>Auto</th><th>Nocleg</th><th>Preferred / max</th><th>Typ</th><th>Status</th><th>Rejestracja</th><th>ID</th></tr></thead>
                <tbody>{data.vehicles.map((vehicle) => (
                  <tr key={vehicle.id}>
                    <td><strong>{vehicle.name}</strong></td>
                    <td><span className="team-base-pill">{vehicle.baseName}</span></td>
                    <td><strong>{vehicle.preferredCapacity} / {vehicle.capacity}</strong></td>
                    <td>{vehicle.setup ? <span className="fleet-type setup">SETUP</span> : <span className="fleet-type normal">NORMAL</span>}</td>
                    <td>{vehicle.active ? <span className="fleet-type normal">AKTYWNE</span> : <span className="fleet-type setup">OFF</span>}</td>
                    <td>{vehicle.registration || <span className="team-muted">—</span>}</td>
                    <td><code>{vehicle.id}</code></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </article>

          <div className="logistics-health-grid">
            {data.accommodations.filter((base) => base.active).map((base) => {
              const members = data.staff.filter((person) => person.baseId === base.id);
              const drivers = members.filter((person) => person.driverEligible).length;
              const normalCars = data.vehicles.filter((vehicle) => vehicle.baseId === base.id && vehicle.active && !vehicle.setup).length;
              const shortage = normalCars > drivers;
              return (
                <article className={`panel logistics-health-card ${shortage ? "warn" : "ok"}`} key={base.id}>
                  <span>{base.name}</span>
                  <strong>{drivers} K / {normalCars} aut</strong>
                  <small>{shortage ? "Mniej kierowców niż normalnych aut" : "Podstawowa dostępność kierowców OK"}</small>
                </article>
              );
            })}
          </div>
        </>
      ) : null}
    </section>
  );
}
